import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import { Avatar } from '@/components/Avatar';

/**
 * Piezas visuales del legacy (`inicio/simulador-v2`, `inicio/index`), con los tokens de los dos
 * temas. Úsalas para que todas las secciones se sientan de la misma familia.
 */

/** Título de bloque: avatar opcional asomado a la izquierda + línea verde centrada debajo. */
export function TituloBloque({
  avatar,
  icono,
  children,
  className,
}: {
  avatar?: string;
  icono?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative mx-auto max-w-xs text-center', className)}>
      {avatar && (
        <Avatar src={avatar} className="absolute bottom-0 left-0 h-20 -translate-x-1/3 sm:h-24" />
      )}
      <h2 className="text-lg font-extrabold text-azul-texto">
        {icono && <span className="mr-1 inline-block align-[-3px]">{icono}</span>}
        {children}
      </h2>
      <div className="mx-auto mt-1 h-[3px] w-4/5 rounded-full bg-verde-500" />
    </div>
  );
}

const VARIANTES = {
  marca: 'bg-marca',
  verde: 'bg-exito',
  accion: 'bg-accion',
} as const;

/**
 * «botonBiColor» del legacy: bloque marino con ícono + cuerpo con degradado. Con `to` es un
 * enlace interno; con `href`, uno externo; con `onClick`, un botón. `activo={false}` lo deja al 50 % (opción no elegida de un grupo).
 */
export function BotonBiColor({
  icono,
  activo = true,
  disabled,
  exclusivo,
  variante = 'marca',
  to,
  href,
  onClick,
  className,
  children,
}: {
  icono: ReactNode;
  activo?: boolean;
  disabled?: boolean;
  exclusivo?: boolean;
  variante?: keyof typeof VARIANTES;
  to?: string;
  /** Enlace externo (se abre en otra pestaña). */
  href?: string;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const clase = cn(
    'flex h-[60px] w-full overflow-hidden rounded-[10px] text-[17px] font-bold text-white no-underline shadow-[5px_0_20px_-10px_#000] transition-all duration-200 active:scale-[0.98] disabled:cursor-not-allowed hover:text-white',
    activo
      ? 'opacity-100 shadow-none disabled:opacity-50'
      : 'opacity-50 hover:opacity-100 disabled:hover:opacity-50',
  );
  const contenido = (
    <>
      <span className="grid w-[30%] place-items-center bg-[#174487]">{icono}</span>
      <span className={cn('grid w-[70%] place-items-center px-2 uppercase', VARIANTES[variante])}>
        {children}
      </span>
    </>
  );
  return (
    <div className={cn('relative mx-auto w-full max-w-[260px]', className)}>
      {to ? (
        <Link to={to} className={clase}>
          {contenido}
        </Link>
      ) : href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={clase}>
          {contenido}
        </a>
      ) : (
        <button
          type="button"
          aria-pressed={onClick && activo !== undefined ? activo : undefined}
          disabled={disabled}
          onClick={onClick}
          className={clase}
        >
          {contenido}
        </button>
      )}
      {exclusivo && (
        <Link
          to="/licencias"
          className="insignia insignia-exclusivo absolute -top-2.5 -right-2.5 no-underline"
        >
          Exclusivo
        </Link>
      )}
    </div>
  );
}

/** Botón grande del inicio legacy: bloque verde con ícono + cuerpo con el degradado de acción. */
export function BotonGrande({
  to,
  icono,
  children,
}: {
  to: string;
  icono: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group flex overflow-hidden rounded-lg text-white no-underline shadow-[var(--shadow-vidrio)] transition-transform hover:-translate-y-0.5 hover:text-white"
    >
      <span className="grid w-16 place-items-center bg-verde-500 text-white">{icono}</span>
      <span className="flex-1 bg-accion px-4 py-3 text-center text-lg font-bold group-hover:brightness-110">
        {children}
      </span>
    </Link>
  );
}

/** Tarjeta con rótulo asomado arriba al centro («card-title-top» del legacy). */
export function TarjetaRotulo({
  rotulo,
  color = 'azul',
  className,
  children,
}: {
  rotulo: string;
  color?: 'azul' | 'verde' | 'marca';
  className?: string;
  children: ReactNode;
}) {
  return (
    <section data-card className={cn('panel-vidrio relative pt-9 lg:pt-10', className)}>
      <h2
        className={cn(
          'rotulo-pill absolute -top-4 left-1/2 w-max max-w-[90%] -translate-x-1/2 text-center text-xs tracking-wider sm:text-sm',
          color === 'verde' && 'bg-exito',
          color === 'marca' && 'bg-marca',
        )}
      >
        {rotulo}
      </h2>
      {children}
    </section>
  );
}

/** Caja de lectura con rótulo verde asomado («justificacion» del legacy). */
export function CajaRotulo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="superficie-lectura relative mt-5 border-2 border-exito px-4 pt-6 pb-4">
      <p className="absolute -top-3 left-4 rounded-md bg-exito px-4 py-0.5 text-xs font-extrabold tracking-wider text-white uppercase">
        {rotulo}
      </p>
      {children}
    </div>
  );
}

/** Punto de color + etiqueta + valor (leyenda de las donas de resultados). */
export function Leyenda({
  color,
  etiqueta,
  valor,
}: {
  color: string;
  etiqueta: string;
  valor: string;
}) {
  return (
    <p className="flex items-center gap-2 text-left">
      <span className="h-6 w-6 shrink-0 rounded-full" style={{ background: color }} />
      <span>
        <span className="block text-xs text-texto-3">{etiqueta}</span>
        <strong className="tabular">{valor}</strong>
      </span>
    </p>
  );
}
